import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  DestroyRef,
  inject,
} from "@angular/core";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {Router, RouterLink, UrlTree} from "@angular/router";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {fromEvent, map} from "rxjs";
import {AppNewsPublic} from "src/app/interfaces/app-news.interface";
import {environment} from "src/environments/environment";

@Component({
  selector: "app-news-content",
  templateUrl: "./news-content.component.html",
  imports: [RouterLink, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewsContentComponent implements OnChanges, OnDestroy {
  @Input({required: true}) news!: AppNewsPublic;
  @Input() language?: string;
  @Input() preview = false;
  @Input() active = true;
  @Input() mediaPreviewUrl = "";
  @Output() dismissed = new EventEmitter<void>();
  @Output() mediaLoadFailed = new EventEmitter<boolean>();
  @ViewChild("video") private video?: ElementRef<HTMLVideoElement>;
  mediaError = false;
  private previousMediaUrl = "";
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly currentLanguage = toSignal(
    this.translate.onLangChange.pipe(map((event) => event.lang)),
    {initialValue: this.translate.currentLang}
  );

  constructor() {
    fromEvent(document, "visibilitychange")
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => {
        if (document.hidden) this.video?.nativeElement.pause();
      });
  }

  get mediaUrl(): string {
    if (this.preview && this.mediaPreviewUrl) return this.mediaPreviewUrl;
    return this.news.media
      ? `${environment.imagesDomain}/news/${this.news.media.filename}`
      : "";
  }

  ngOnChanges(): void {
    if (this.previousMediaUrl !== this.mediaUrl) {
      this.previousMediaUrl = this.mediaUrl;
      this.mediaError = false;
    }
    if (!this.active) this.video?.nativeElement.pause();
  }

  mediaFailed(): void {
    this.mediaError = true;
    this.mediaLoadFailed.emit(true);
  }

  videoLoaded(video: HTMLVideoElement): void {
    if (
      !Number.isFinite(video.duration) ||
      video.duration > 30 ||
      !video.duration ||
      Math.max(video.videoWidth, video.videoHeight) > 1920
    ) {
      video.pause();
      this.mediaFailed();
      return;
    }
    this.mediaLoadFailed.emit(false);
    if (!this.active || document.hidden) video.pause();
  }

  ngOnDestroy(): void {
    this.video?.nativeElement.pause();
  }

  get text() {
    const english = (
      this.language ??
      this.currentLanguage() ??
      "fr"
    ).startsWith("en");
    return english && this.news.en ? this.news.en : this.news.fr;
  }

  get internalLink(): UrlTree | null {
    return this.preview ? null : this.router.parseUrl(this.news.ctaUrl);
  }
}
